/**
 * NetworkStatusBanner Component
 *
 * 全局網絡狀態提示：
 * 1. 離線時持續顯示
 * 2. 回復連線時短暫顯示
 * 3. 補充最後更新時間
 */

import React, { memo, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import { useLanguage } from '../../contexts/LanguageContext';
import { useDictionary } from '../../contexts/DictionaryContext';

const NOTICE_DURATION_MS = 8000;

const formatDateTime = (value, language) => {
  if (!value) return null;

  try {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString(language === 'tc' ? 'zh-HK' : 'en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch (error) {
    return null;
  }
};

const NetworkStatusBanner = memo(function NetworkStatusBanner() {
  const { language } = useLanguage();
  const { lastUpdate } = useDictionary();
  const [isOnline, setIsOnline] = useState(() => {
    if (typeof navigator === 'undefined') return true;
    return navigator.onLine;
  });
  const [showOnlineNotice, setShowOnlineNotice] = useState(false);
  const [lastConnectionChange, setLastConnectionChange] = useState(() => new Date());

  useEffect(() => {
    let timer = null;

    const handleOnline = () => {
      setIsOnline(true);
      setLastConnectionChange(new Date());
      setShowOnlineNotice(true);
      timer = setTimeout(() => {
        setShowOnlineNotice(false);
      }, NOTICE_DURATION_MS);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowOnlineNotice(false);
      setLastConnectionChange(new Date());
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const latestTimestamp = useMemo(() => {
    return lastUpdate || lastConnectionChange;
  }, [lastConnectionChange, lastUpdate]);

  const formattedTimestamp = useMemo(() => {
    return formatDateTime(latestTimestamp, language);
  }, [language, latestTimestamp]);

  if (isOnline && !showOnlineNotice) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: { xs: '64px', sm: '72px' },
        left: 0,
        right: 0,
        zIndex: 1190,
        px: { xs: 1, sm: 2, md: 3 },
        pt: 1,
      }}
    >
      <Alert
        severity={isOnline ? 'success' : 'warning'}
        icon={isOnline ? <CloudDoneIcon /> : <CloudOffIcon />}
        role="status"
        aria-live="polite"
        sx={{
          borderRadius: 2,
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {isOnline
            ? (language === 'tc' ? '網絡連線已恢復' : 'Network connection restored')
            : (language === 'tc' ? '目前處於離線狀態，資料可能不是最新' : 'You are offline. Data may not be up to date')}
        </Typography>
        {formattedTimestamp && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
            {language === 'tc' ? '最後更新：' : 'Last updated: '}
            {formattedTimestamp}
          </Typography>
        )}
      </Alert>
    </Box>
  );
});

export default NetworkStatusBanner;
